import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    LinearProgress,
    Pagination,
    Stack,
    Select,
    MenuItem,
    FormControl,
    Chip,
    Collapse,
    Tooltip,
    IconButton,
    useMediaQuery,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ArticleIcon from '@mui/icons-material/Article';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import KzqTicketRegistryItem from '@LabelAnalyticsDashboardsLabelViewCharts/KzqTicketRegistryItem';
import ErrorHandler from '@Root/ErrorHandler';
import KzqTicketRegistryViewService from '@Helpers/URLHelpers/KaiziqResolve/views/KzqTicketRegistryViewService';
import './LabelViewUsageList.scss';

const DATE_PRESETS = [
    { label: 'All Time', id: 'all' },
    { label: 'Today', id: 'today' },
    { label: 'This Week', id: 'thisWeek' },
    { label: 'Last Week', id: 'lastWeek' },
    { label: 'Last Month', id: 'lastMonth' },
    { label: 'Last 3 Months', id: 'last3months' },
    { label: 'Custom', id: 'custom' },
];

const getPresetRange = (presetId) => {
    const now = new Date();
    switch (presetId) {
        case 'all':
            return { from: null, to: null };
        case 'today': {
            const start = new Date(now); start.setHours(0, 0, 0, 0);
            return { from: start.getTime(), to: Date.now() };
        }
        case 'thisWeek': {
            const start = new Date(now);
            start.setDate(now.getDate() - now.getDay());
            start.setHours(0, 0, 0, 0);
            return { from: start.getTime(), to: Date.now() };
        }
        case 'lastWeek': {
            const start = new Date(now);
            start.setDate(now.getDate() - now.getDay() - 7);
            start.setHours(0, 0, 0, 0);
            const end = new Date(start);
            end.setDate(start.getDate() + 6);
            end.setHours(23, 59, 59, 999);
            return { from: start.getTime(), to: end.getTime() };
        }
        case 'lastMonth': {
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
            return { from: start.getTime(), to: end.getTime() };
        }
        case 'last3months': {
            const start = new Date(now);
            start.setMonth(now.getMonth() - 3);
            start.setHours(0, 0, 0, 0);
            return { from: start.getTime(), to: Date.now() };
        }
        default:
            return { from: null, to: null };
    }
};

export default function LabelViewUsageList(props) {
    const [labelViewList, setLabelViewList] = useState([]);
    const [showSearchLoader, setShowSearchLoader] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalResult, setTotalResult] = useState(0);
    const [activePreset, setActivePreset] = useState('all');
    const [showCustomPicker, setShowCustomPicker] = useState(false);
    const [customFrom, setCustomFrom] = useState(null);
    const [customTo, setCustomTo] = useState(null);
    const [dateRange, setDateRange] = useState({ from: null, to: null });

    const isMobile = useMediaQuery('(max-width: 768px)');
    const kzqTicketRegistryViewService = new KzqTicketRegistryViewService();
    const errorHandler = new ErrorHandler();
    const className = 'labelViewUsageList';

    useEffect(() => {
        renderData();
    }, [props?.labelData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const buildFilterObj = (pg, sz, range) => {
        const filter = {
            registryId:   props?.labelData?.id,
            registryType: props?.labelData?.registryType,
        };
        if (range.from) filter.dateFrom = range.from;
        if (range.to)   filter.dateTo   = range.to;
        return { kzqTicketRegistryViewFilter: filter, page: pg - 1, size: sz };
    };

    const parseResponse = (data) => {
        const outer = JSON.parse(data?.body);
        return JSON.parse(outer?.body);
    };

    const renderData = async () => {
        try {
            setShowSearchLoader(true);
            const f = buildFilterObj(1, size, dateRange);
            const data = await kzqTicketRegistryViewService.getListFiltered(f);
            const res = parseResponse(data);
            setLabelViewList(res?.result?.ticketRegistryList || []);
            const items = Number(res?.result?.totalItems ?? 0);
            setTotalPages(Number(res?.result?.totalPages ?? Math.ceil(items / size)) || 1);
            setTotalResult(items);
            setPage(1);
        } catch (err) {
            errorHandler.handleError(err);
        }
        setShowSearchLoader(false);
    };

    const fetchPage = async (pg, sz, range) => {
        try {
            setShowSearchLoader(true);
            const f = buildFilterObj(pg, sz, range);
            const data = await kzqTicketRegistryViewService.getListFiltered(f);
            const res = parseResponse(data);
            setLabelViewList(res?.result?.ticketRegistryList || []);
            const items = Number(res?.result?.totalItems ?? 0);
            setTotalPages(Number(res?.result?.totalPages ?? Math.ceil(items / sz)) || 1);
            setTotalResult(items);
        } catch (err) {
            errorHandler.handleError(err);
        }
        setShowSearchLoader(false);
    };

    const handlePresetClick = (presetId) => {
        setActivePreset(presetId);
        if (presetId === 'custom') {
            setShowCustomPicker(true);
            return;
        }
        setShowCustomPicker(false);
        const range = getPresetRange(presetId);
        setDateRange(range);
        setPage(1);
        fetchPage(1, size, range);
    };

    const handleCustomApply = () => {
        if (!customFrom || !customTo) return;
        const range = {
            from: customFrom.startOf('day').valueOf(),
            to: customTo.endOf('day').valueOf(),
        };
        setDateRange(range);
        setPage(1);
        fetchPage(1, size, range);
    };

    const handlePaginationClick = (e, v) => {
        setPage(v);
        fetchPage(v, size, dateRange);
    };

    const handleSizeChange = (event) => {
        const newSize = event.target.value;
        setSize(newSize);
        setPage(1);
        fetchPage(1, newSize, dateRange);
    };

    const handleExcelExport = async () => {
        try {
            setIsExporting(true);
            const filterObj = {
                kzqTicketRegistryViewFilter: {
                    registryId:   props?.labelData?.id,
                    registryType: props?.labelData?.registryType,
                    ...(dateRange?.from ? { dateFrom: dateRange.from } : {}),
                    ...(dateRange?.to   ? { dateTo:   dateRange.to   } : {}),
                },
            };
            const data = await kzqTicketRegistryViewService.export(filterObj);
            const outer = JSON.parse(data?.body);
            const res = JSON.parse(outer?.body);
            const raw = res?.file;
            if (!raw) return;
            const base64 = raw.startsWith('data:') ? raw.split(',')[1] : raw;
            const binary = atob(base64.replace(/\s/g, ''));
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = res?.fileName || `${(props?.labelData?.name || 'card').replace(/[^\w-]/g, '_')}_usage.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            errorHandler.handleError(err);
        }
        setIsExporting(false);
    };

    const activePresetLabel = DATE_PRESETS.find(p => p.id === activePreset)?.label;

    return (
        <div className={className}>
            {/* Header */}
            <Box className={`${className}__header`}>
                <Box className={`${className}__header__left`}>
                    <FormatListBulletedIcon className={`${className}__header__icon`} />
                    <Box>
                        <Typography variant="h6" className={`${className}__header__title`}>
                            Card Usage List
                        </Typography>
                        <Typography variant="caption" className={`${className}__header__subtitle`}>
                            Track how and when your cards are being used
                        </Typography>
                    </Box>
                </Box>
                <Box className={`${className}__header__right`}>
                    {activePreset !== 'all' && (
                        <Chip
                            label={activePresetLabel}
                            size="small"
                            className={`${className}__header__filterChip`}
                            onDelete={() => handlePresetClick('all')}
                        />
                    )}
                    <Chip
                        icon={<ArticleIcon />}
                        label={`${totalResult} ${totalResult === 1 ? 'Record' : 'Records'}`}
                        className={`${className}__header__countChip`}
                        color="primary"
                        variant="outlined"
                    />
                    <Tooltip title={isExporting ? 'Exporting...' : 'Export Excel'}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleExcelExport}
                                disabled={isExporting || totalResult === 0}
                                className={`${className}__exportBtn`}
                            >
                                <FileDownloadOutlinedIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {/* Filter Bar */}
            <Box className={`${className}__filterBar`}>
                <FilterListIcon className={`${className}__filterBar__icon`} />
                <Box className={`${className}__filterBar__presets`}>
                    {DATE_PRESETS.map(preset => (
                        <button
                            key={preset.id}
                            className={`${className}__filterBar__preset${activePreset === preset.id ? ' active' : ''}`}
                            onClick={() => handlePresetClick(preset.id)}
                        >
                            {preset.label}
                        </button>
                    ))}
                </Box>
            </Box>

            {/* Custom date range */}
            <Collapse in={showCustomPicker && activePreset === 'custom'}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box className={`${className}__customRange`}>
                        <DatePicker
                            label="From"
                            value={customFrom}
                            onChange={setCustomFrom}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    className: `${className}__customRange__picker`,
                                }
                            }}
                        />
                        <DatePicker
                            label="To"
                            value={customTo}
                            onChange={setCustomTo}
                            minDate={customFrom}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    className: `${className}__customRange__picker`,
                                }
                            }}
                        />
                        <button
                            className={`${className}__customRange__applyBtn`}
                            onClick={handleCustomApply}
                            disabled={!customFrom || !customTo}
                        >
                            Apply Range
                        </button>
                    </Box>
                </LocalizationProvider>
            </Collapse>

            {/* Content */}
            <Box className={`${className}__content`}>
                {showSearchLoader ? (
                    <Box className={`${className}__loader`}>
                        <LinearProgress />
                        <Typography variant="body2" color="textSecondary" className={`${className}__loader__text`}>
                            Loading usage data...
                        </Typography>
                    </Box>
                ) : labelViewList.length === 0 ? (
                    <Box className={`${className}__empty`}>
                        <ArticleIcon className={`${className}__empty__icon`} />
                        <Typography variant="h6" className={`${className}__empty__title`}>
                            No Usage Data
                        </Typography>
                        <Typography variant="body2" color="textSecondary" className={`${className}__empty__subtitle`}>
                            {activePreset !== 'all'
                                ? 'No records match the selected date range.'
                                : 'Data will appear here once labels are used.'}
                        </Typography>
                    </Box>
                ) : (
                    <Box className={`${className}__list`}>
                        {labelViewList.map((item, index) => (
                            <KzqTicketRegistryItem key={item.rn ?? index} item={item} />
                        ))}
                    </Box>
                )}
            </Box>

            {/* Pagination */}
            {!showSearchLoader && (
                <Box className={`${className}__pagination`}>
                    <Box className={`${className}__pagination__controls`}>
                        <Stack spacing={1}>
                            <Pagination
                                count={Math.max(totalPages, 1)}
                                page={page}
                                onChange={handlePaginationClick}
                                showFirstButton
                                showLastButton
                                size={isMobile ? 'small' : 'medium'}
                                siblingCount={isMobile ? 0 : 1}
                                className={`${className}__pagination__nav`}
                            />
                        </Stack>
                        <Box className={`${className}__pagination__size`}>
                            <Typography variant="caption" className={`${className}__pagination__size__label`}>
                                Per page:
                            </Typography>
                            <FormControl size="small">
                                <Select
                                    value={size}
                                    onChange={handleSizeChange}
                                    className={`${className}__pagination__size__select`}
                                >
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={25}>25</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                </Box>
            )}
        </div>
    );
}
